import { useState, useEffect } from 'react'
import { templatesApi } from '@/services/api'

export interface CurrentTemplate {
  id: string
  name: string
  businessType: string
  description?: string
  isActive: boolean
  industry?: string
  complexity?: string
  tags?: string[]
  version?: string
  createdAt: string
  updatedAt: string
  customBusinessName?: string | null
  categoriesCount?: number
  projectTypesCount?: number
  activityTypesCount?: number
  unitTypesCount?: number
}

export const useCurrentTemplate = () => {
  const [template, setTemplate] = useState<CurrentTemplate | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchCurrentTemplate = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Use lightweight endpoint for sidebar - no heavy queries
      const templateData = await templatesApi.getUserDefaultTemplateWithCustomName()
      
      if (templateData) {
        const template: CurrentTemplate = {
          id: templateData.id,
          name: templateData.name,
          businessType: templateData.businessType,
          description: templateData.description,
          isActive: templateData.isActive,
          industry: templateData.industry,
          complexity: templateData.complexity,
          tags: templateData.tags,
          version: templateData.version,
          createdAt: templateData.createdAt,
          updatedAt: templateData.updatedAt,
          customBusinessName: templateData.customBusinessName,
          // These counts are not needed for sidebar, so we don't fetch them
          categoriesCount: 0,
          projectTypesCount: 0,
          activityTypesCount: 0,
          unitTypesCount: 0,
        }
        setTemplate(template)
      } else {
        setTemplate(null)
      }
    } catch (err) {
      console.error('Failed to fetch current template:', err)
      setError('Failed to load current template')
      setTemplate(null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchCurrentTemplate()
    
    // Listen for template change events
    const handleTemplateChange = () => {
      fetchCurrentTemplate()
    }
    
    window.addEventListener('templateChanged', handleTemplateChange)
    
    return () => {
      window.removeEventListener('templateChanged', handleTemplateChange)
    }
  }, [])

  return {
    template,
    loading,
    error,
    refetch: fetchCurrentTemplate
  }
}